import React, { useContext, useEffect, useState, createContext } from "react";
import { db } from '../firebase';
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";

import { useAuth } from './AuthContexts';

// Creating User context to avoid multiple BD access
const UserContext = createContext();

export const useUser = () => {
    return useContext(UserContext);
}

export const UserProvider = ({ children }) => {

    const { currentUser } = useAuth();

    const [ loading, setLoading ] = useState(false);

    // State with all user information
    const [ userInfo, setUserInfo ] = useState({
        dietDaysCount: 0,
        dietDaysMap: {},
        trainingDaysCount: 0,
        trainingDaysMap: {},
        cardioDaysCount: 0,
        cardioDaysMap: {},
        name: "",
        currentWeight: 0,
        dietKcal: 0,
        height: 0,
        weekTrains: 0
    });

    // All functions first get info from BD and after changes context states
    // Track user training and dieting
    const track = async (dietCheck, trainCheck, cardioCheck, selectedWeekDay) => {

        if(!currentUser || !currentUser.uid) return;

        try{
            const userData = userInfo;

            const currentDate = new Date();
            const markedWeekDay = new Date(currentDate.setDate(currentDate.getDate() - (currentDate.getDay() + 6)%7));
            markedWeekDay.setDate(markedWeekDay.getDate() + selectedWeekDay)
            const isoDate = String(markedWeekDay.getFullYear() + "-" + String(parseInt(markedWeekDay.getMonth() + 1)).padStart(2, '0') + "-" + String(markedWeekDay.getDate()).padStart(2, '0'));

            const nTrainingDays = userData.trainingDaysCount;
            const nDietDays = userData.dietDaysCount;
            const nCardioDays = userData.cardioDaysCount;

            if(!!userData){
                await setDoc(doc(db, "users", currentUser.uid), {
                    dietDaysMap: {
                        [isoDate] : dietCheck,
                    },
                    dietDaysCount: (userData.dietDaysMap[isoDate] || false) !== dietCheck ? (dietCheck ? nDietDays+1 : nDietDays-1) : nDietDays,
                }, {merge: true});
                
                await setDoc(doc(db, "users", currentUser.uid), {
                    trainingDaysMap: {
                        [isoDate] : trainCheck,
                    },
                    trainingDaysCount: (userData.trainingDaysMap[isoDate] || false) !== trainCheck ? (trainCheck ? nTrainingDays+1 : nTrainingDays-1) : nTrainingDays,
                }, {merge: true});

                await setDoc(doc(db, "users", currentUser.uid), {
                    cardioDaysMap: {
                        [isoDate] : cardioCheck,
                    },
                    cardioDaysCount: (userData.cardioDaysMap[isoDate] || false) !== cardioCheck ? (cardioCheck ? nCardioDays+1 : nCardioDays-1) : nCardioDays,
                }, {merge: true});
            } else {
                throw "Error(Track) - user data is empty"
            }

            // Updating context state after changes in DB
            setUserInfo({
                ...userInfo,
                dietDaysMap: {
                    ...userInfo.dietDaysMap,
                    [isoDate]: dietCheck,
                },
                dietDaysCount: (userData.dietDaysMap[isoDate] || false) !== dietCheck ? (dietCheck ? nDietDays+1 : nDietDays-1) : nDietDays,
                trainingDaysMap: {
                    ...userInfo.trainingDaysMap,
                    [isoDate]: trainCheck,
                },  
                trainingDaysCount: (userData.trainingDaysMap[isoDate] || false) !== trainCheck ? (trainCheck ? nTrainingDays+1 : nTrainingDays-1) : nTrainingDays,
                cardioDaysMap: {
                    ...userInfo.cardioDaysMap,
                    [isoDate]: cardioCheck,
                },  
                cardioDaysCount: (userData.cardioDaysMap[isoDate] || false) !== cardioCheck ? (cardioCheck ? nCardioDays+1 : nCardioDays-1) : nCardioDays,
            })
        } catch(e) {
            throw "Error(Track) no Track."
        }
    };

    // Update user profile informations
    const updateProfile = async (currentName, currentWeight, currentHeight, currentTrains, currentKcal) => {
        if(!currentUser || !currentUser.uid) return;
    
        if(!currentName || !currentWeight || !currentHeight || !currentTrains || !currentKcal) throw "Erro nos campos de texto";
        if(!/^\d\d\d?(\.\d\d?)?$/gm.test(currentWeight)) throw "Erro no Peso - formato: xx.xx";
        if(!/^\d\d\d$/gm.test(currentHeight)) throw "Erro na Altura - formato: xxx";
        if(!/^\d\d?$/gm.test(currentTrains)) throw "Erro no Treino - formato: x";
        if(!/^\d\d\d\d$/gm.test(currentKcal)) throw "Erro nas calorias - formato: xxxx";
        if (!currentName.trim() || currentName.trim().length<2 || currentName.length>50) throw "Nome inválido";
        
        try {
            await updateDoc(doc(db, "users", currentUser.uid), {
                name: currentName,
                currentWeight: parseFloat(currentWeight),
                height: parseInt(currentHeight),
                weekTrains: parseInt(currentTrains),
                dietKcal: parseInt(currentKcal),
            });

            // updating context
            setUserInfo({
                ...userInfo,
                name: currentName,
                currentWeight: parseFloat(currentWeight),
                height: parseInt(currentHeight),
                weekTrains: parseInt(currentTrains),
                dietKcal: parseInt(currentKcal),
            });

            
        } catch(e) {
            throw "Error(update profile)";
        }
    }

    // Get BD information
    const getUserInfo = async () => {
        if(!currentUser || !currentUser.uid) return;
        setLoading(true);

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));

        try {
            if(userSnap.exists()) {
                const userData = userSnap.data();

                const auxUserData = {
                    dietDaysCount: userData.dietDaysCount,
                    dietDaysMap: userData.dietDaysMap,
                    trainingDaysCount: userData.trainingDaysCount,
                    trainingDaysMap: userData.trainingDaysMap,
                    cardioDaysCount: userData.cardioDaysCount,
                    cardioDaysMap: userData.cardioDaysMap,
                    name: userData.name,
                    currentWeight: userData.currentWeight,
                    dietKcal: userData.dietKcal,
                    height: userData.height,
                    weekTrains: userData.weekTrains
                }

                setUserInfo(auxUserData);

                setLoading(false);
            } else{
                console.log("Error(getUserInfo)", "error");
            }
        } catch (e) {
            console.log("Error(getUserInfo)", e);
        }
        setLoading(false);
    }

    useEffect(() => {
        getUserInfo(currentUser);
    }, [currentUser])
    
    const value = {
        userInfo,
        track,
        updateProfile
    }
    
    return (
        <UserContext.Provider value={value}>
            { !loading && children }
        </UserContext.Provider>
    )
}
